import React, { useState } from 'react';
import { CookieConsent, getCookieConsentValue } from 'react-cookie-consent';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  cookieConsentName as canonicalCookieConsentName,
  STORAGE_KEY_ALIASES,
} from '~/app/storageKeys';
import { consentCookieSchema } from '~/app/storageSchemas';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '~/components/Dialog/Dialog';
import { Button } from '~/components/Form/Button';

import { useTranslation } from './i18n-context';
import { ROUTE_PRIVACY, ROUTE_TERMS } from './routes';

export const cookieConsentName = canonicalCookieConsentName;
export const legacyCookieConsentName = STORAGE_KEY_ALIASES.cookieConsent[0];

const COOKIE_EXPIRY_DAYS = 150;

const setConsentCookie = (value: string): void => {
  const validated = consentCookieSchema.safeParse(value);
  if (!validated.success) return;
  const maxAge = COOKIE_EXPIRY_DAYS * 24 * 60 * 60;
  document.cookie = `${cookieConsentName}=${validated.data}; path=/; max-age=${maxAge}; SameSite=Lax`;
  // Keep legacy cookie name during migration window.
  // TODO: [storage-migration-cleanup] Stop writing legacy consent cookie when safe.
  document.cookie = `${legacyCookieConsentName}=${validated.data}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

interface CookieConsentBannerProps {
  onDialogOpenChange?: (open: boolean) => void;
}

const CookieConsentBanner = ({ onDialogOpenChange }: CookieConsentBannerProps): JSX.Element => {
  const { t } = useTranslation();
  const firebaseAnalytics = useFirebaseAnalytics();
  const [dialogOpen, setDialogOpenState] = useState(false);

  React.useEffect(() => {
    // Legacy fallback: if only old cookie exists, mirror it into canonical cookie name.
    // TODO: [storage-migration-cleanup] Remove legacy consent cookie fallback after rollout.
    const canonical = consentCookieSchema.safeParse(getCookieConsentValue(cookieConsentName));
    const legacy = consentCookieSchema.safeParse(getCookieConsentValue(legacyCookieConsentName));
    if (!canonical.success && legacy.success) {
      setConsentCookie(legacy.data);
    }
  }, []);

  const setDialogOpen = (open: boolean) => {
    setDialogOpenState(open);
    onDialogOpenChange?.(open);
  };

  const enableAnalytics = () => {
    if (firebaseAnalytics?.analytics) {
      firebaseAnalytics.setAnalyticsCollectionEnabled(true);
    }
  };

  const disableAnalytics = () => {
    if (firebaseAnalytics?.analytics) {
      firebaseAnalytics.setAnalyticsCollectionEnabled(false);
    }
  };

  const handleAcceptFromDialog = () => {
    setConsentCookie('true');
    enableAnalytics();
    setDialogOpen(false);
  };

  const fullContent = (
    <>
      {t('start_cookieConsentGranted_1')}{' '}
      <a
        href={ROUTE_PRIVACY}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-primary underline hover:text-primary-light"
      >
        {t('datenschutz_title')}
      </a>{' '}
      {t('start_cookieConsentGranted_and')}{' '}
      <a
        href={ROUTE_TERMS}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-primary underline hover:text-primary-light"
      >
        {t('agbs_title')}
      </a>{' '}
      {t('start_cookieConsentGranted_3')}
    </>
  );

  return (
    <>
      {!dialogOpen && (
        <CookieConsent
          buttonText={t('start_cookieConsentGrantedAccept')}
          cookieName={cookieConsentName}
          buttonClasses="bg-secondary mx-2 px-3 py-2 rounded-md shadow-xs"
          contentClasses="flex-1 flex items-center gap-3 min-w-0 text-gray-50 text-sm"
          containerClasses="fixed px-8 items-center flex flex-row py-4 inset-x-0 bottom-0 z-100 bg-primary-dark/85 backdrop-blur-xl"
          disableStyles={true}
          expires={COOKIE_EXPIRY_DAYS}
          onAccept={enableAnalytics}
          onDecline={disableAnalytics}
        >
          <span className="line-clamp-3 min-w-0">
            {t('start_cookieConsentGranted_1')}{' '}
            <a
              href={ROUTE_PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-secondary underline"
            >
              {t('datenschutz_title')}
            </a>{' '}
            {t('start_cookieConsentGranted_and')}{' '}
            <a
              href={ROUTE_TERMS}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-secondary underline"
            >
              {t('agbs_title')}
            </a>{' '}
            {t('start_cookieConsentGranted_3')}
          </span>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="shrink-0 rounded-md border border-gray-50/30 bg-white/10 px-3 py-2 text-gray-50 hover:bg-white/20 focus:ring-2 focus:ring-secondary focus:ring-offset-2 focus:ring-offset-primary-dark/85 focus:outline-none"
          >
            {t('start_cookieConsentDetails')}
          </button>
        </CookieConsent>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="lg">
          <DialogTitle>{t('start_cookieConsentDialogTitle')}</DialogTitle>
          <DialogDescription className="selectable-text">{fullContent}</DialogDescription>
          <div className="mt-6 flex flex-nowrap justify-end space-x-3">
            <DialogClose asChild>
              <Button variant="secondary">{t('cancel')}</Button>
            </DialogClose>
            <Button variant="primary" onClick={handleAcceptFromDialog}>
              {t('start_cookieConsentGrantedAccept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
